'use client'

import { useState, useEffect, ReactNode } from 'react'

const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'
const OUT_MS = 160
const IN_MS = 220

interface FadeThroughProps {
  stageKey: string
  children: ReactNode
  className?: string
  delay?: number  // stagger offset in ms for fade-in
}

export function FadeThrough({ stageKey, children, className, delay = 0 }: FadeThroughProps) {
  const [displayed, setDisplayed] = useState(children)
  const [phase, setPhase] = useState<'stable' | 'out' | 'ready'>('stable')

  useEffect(() => {
    setPhase('out')

    const t = setTimeout(() => {
      setDisplayed(children)
      setPhase('ready')
      // Two rAFs: first ensures the 'ready' style is committed, second triggers the transition
      requestAnimationFrame(() => requestAnimationFrame(() => setPhase('stable')))
    }, OUT_MS)

    return () => clearTimeout(t)
  }, [stageKey])

  const style: React.CSSProperties =
    phase === 'out'
      ? { opacity: 0, transform: 'translateY(-8px)', transition: `opacity ${OUT_MS}ms ${EASING}, transform ${OUT_MS}ms ${EASING}` }
      : phase === 'ready'
      ? { opacity: 0, transform: 'translateY(8px)', transition: 'none' }
      : { opacity: 1, transform: 'translateY(0)', transition: `opacity ${IN_MS}ms ${EASING} ${delay}ms, transform ${IN_MS}ms ${EASING} ${delay}ms` }

  return (
    <span className={className} style={{ display: 'block', ...style }}>
      {displayed}
    </span>
  )
}
